import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, useConfig, homeFor } from "@/lib/auth";
import { Field } from "@/components/xd/ui";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/xd/PasswordInput";
import { AuthFrame, PhoneInput, CodeInput, LoginHelp, helpUrl } from "./Login";

const useRunner = () => {
  const { tErr } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(tErr(e)); } finally { setBusy(false); }
  };
  return [busy, run];
};

const ApplySuccess = ({ role }) => {
  const { t } = useLang();
  const { logout } = useAuth();
  return (
    <div className="space-y-5" data-testid={`apply-${role}-success`}>
      <CheckCircle2 size={44} className="text-emerald-400" />
      <h2 className="text-2xl font-semibold chrome-text" data-testid="apply-success-title">{t("applied.title")}</h2>
      <div className="space-y-2 text-zinc-300 leading-relaxed text-sm md:text-base">
        {[1, 2, 3, 4].map((n) => <p key={n}>{t(`applied.p${n}`)}</p>)}
      </div>
      <Link to="/" onClick={() => logout()} className="btn-ghost w-full justify-center" data-testid="apply-success-home">{t("applied.home")}</Link>
    </div>
  );
};

export function Apply({ role }) {
  const { t } = useLang();
  const { setUser } = useAuth();
  const { sms_enabled } = useConfig();
  const [form, setForm] = useState({ name: "", first_name: "", last_name: "", business_name: "", phone: "", email: "", password: "" });
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [done, setDone] = useState(false);
  const [busy, run] = useRunner();
  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const locked = sent && sms_enabled;

  const sendCode = (e) => {
    e.preventDefault();
    run(async () => { await api.post("/auth/otp/send", { phone: form.phone, purpose: "register" }); setSent(true); toast.success(t("auth.code_sent")); });
  };
  const register = (e) => {
    e.preventDefault();
    run(async () => {
      const name = role === "broker" ? `${form.first_name.trim()} ${form.last_name.trim()}` : form.name;
      const { data } = await api.post("/auth/register", { ...form, name, email: form.email || null, business_name: form.business_name || null, role, code: sms_enabled ? code : null });
      setUser(data.user);
      setDone(true);
    });
  };
  if (done) return <AuthFrame title={t(`apply.title_${role}`)}><ApplySuccess role={role} /></AuthFrame>;

  return (
    <AuthFrame title={t(`apply.title_${role}`)} subtitle={t("apply.subtitle")}>
      <form onSubmit={sms_enabled && !sent ? sendCode : register} className="space-y-5" data-testid={`apply-${role}-form`}>
        {role === "dealer" && <Field label={t("form.business_name")}><Input required className="xd-input" value={form.business_name} onChange={up("business_name")} data-testid="apply-business-input" disabled={locked} /></Field>}
        {role === "broker" ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.first_name")}><Input required minLength={2} maxLength={40} className="xd-input" value={form.first_name} onChange={up("first_name")} data-testid="apply-first-name-input" disabled={locked} /></Field>
            <Field label={t("form.last_name")}><Input required minLength={2} maxLength={40} className="xd-input" value={form.last_name} onChange={up("last_name")} data-testid="apply-last-name-input" disabled={locked} /></Field>
          </div>
        ) : <Field label={t("form.contact_person")}><Input required className="xd-input" value={form.name} onChange={up("name")} data-testid="apply-name-input" disabled={locked} /></Field>}
        <Field label={t("form.phone")} hint={t(sms_enabled ? "apply.phone_hint" : "apply.phone_hint_manual")}><PhoneInput required value={form.phone} onChange={up("phone")} data-testid="apply-phone-input" disabled={locked} /></Field>
        <Field label={t("form.email_optional")}><Input type="email" dir="ltr" className="xd-input" value={form.email} onChange={up("email")} data-testid="apply-email-input" disabled={locked} /></Field>
        <Field label={t("form.password")} hint={t("form.password_hint")}><PasswordInput required minLength={8} autoComplete="new-password" value={form.password} onChange={up("password")} testid="apply-password-input" disabled={locked} /></Field>
        {locked && <Field label={t("auth.code")}><CodeInput required value={code} onChange={(e) => setCode(e.target.value)} data-testid="apply-code-input" /></Field>}
        <button disabled={busy} className="btn-xd w-full" data-testid={sms_enabled && !sent ? "apply-send-code-button" : "apply-submit-button"}>{sms_enabled && !sent ? t("auth.send_code") : t("apply.submit")}</button>
        <p className="text-xs text-zinc-500" data-testid="apply-legal-notice">{t("legal.apply_notice")} <Link to="/terms" className="underline hover:text-white" data-testid="apply-terms-link">{t("page.terms")}</Link> · <Link to="/privacy" className="underline hover:text-white" data-testid="apply-privacy-link">{t("page.privacy")}</Link></p>
        <p className="text-xs text-zinc-500">{t("apply.approval_note")}</p>
        <Link to={`/login?as=${role}`} className="block text-sm text-zinc-500 hover:text-white" data-testid="apply-login-link">{t("apply.have_account")}</Link>
      </form>
    </AuthFrame>
  );
}

const ManualReset = () => {
  const { t } = useLang();
  const { whatsapp } = useConfig();
  return (
    <div className="space-y-5" data-testid="reset-manual">
      <p className="text-sm text-zinc-300 leading-relaxed">{t("reset.manual_text")}</p>
      <ol className="text-sm text-zinc-400 list-decimal ps-5 space-y-1.5">{[1, 2, 3].map((n) => <li key={n}>{t(`reset.manual_step${n}`)}</li>)}</ol>
      <a href={helpUrl(whatsapp, t)} target="_blank" rel="noopener noreferrer" className="btn-xd w-full justify-center" data-testid="reset-whatsapp-button"><MessageCircle size={17} />{t("help.contact_whatsapp")}</a>
      <Link to="/login" className="block text-sm text-zinc-500 hover:text-white" data-testid="reset-back-login">{t("auth.login")}</Link>
    </div>
  );
};

export function Reset() {
  const { t } = useLang();
  const { sms_enabled } = useConfig();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, run] = useRunner();
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (!sent) { await api.post("/auth/otp/send", { phone, purpose: "reset" }); setSent(true); toast.success(t("auth.code_sent")); return; }
      await api.post("/auth/password/reset", { phone, code, password });
      toast.success(t("reset.done"));
      navigate("/login", { replace: true });
    });
  };
  if (!sms_enabled) return <AuthFrame title={t("reset.title")}><ManualReset /></AuthFrame>;
  return (
    <AuthFrame title={t("reset.title")} subtitle={t("reset.subtitle")}>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <Field label={t("form.phone")}><PhoneInput required value={phone} onChange={(e) => setPhone(e.target.value)} disabled={sent} data-testid="reset-phone-input" /></Field>
        {sent && (
          <>
            <Field label={t("auth.code")}><CodeInput required value={code} onChange={(e) => setCode(e.target.value)} data-testid="reset-code-input" /></Field>
            <Field label={t("reset.new_password")} hint={t("form.password_hint")}><PasswordInput required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} testid="reset-password-input" /></Field>
          </>
        )}
        <button disabled={busy} className="btn-xd w-full" data-testid="reset-submit-button">{sent ? t("reset.submit") : t("auth.send_code")}</button>
        <LoginHelp testid="reset-help-whatsapp" />
      </form>
    </AuthFrame>
  );
}

export function ChangePassword() {
  const { t } = useLang();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, run] = useRunner();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  const submit = (e) => {
    e.preventDefault();
    if (f.next !== f.confirm) return toast.error(t("err.password_mismatch"));
    run(async () => {
      const { data } = await api.post("/auth/password/change", { current_password: f.current, new_password: f.next });
      setUser(data.user);
      toast.success(t("pwchange.done"));
      navigate(homeFor(data.user), { replace: true });
    });
  };
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthFrame title={t("pwchange.title")} subtitle={t(user.must_change_password ? "pwchange.forced" : "pwchange.subtitle")}>
      <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
        <Field label={t(user.must_change_password ? "pwchange.temp" : "pwchange.current")}><PasswordInput required autoComplete="current-password" value={f.current} onChange={up("current")} testid="change-current-input" /></Field>
        <Field label={t("reset.new_password")} hint={t("form.password_hint")}><PasswordInput required minLength={8} autoComplete="new-password" value={f.next} onChange={up("next")} testid="change-new-input" /></Field>
        <Field label={t("pwchange.confirm")}><PasswordInput required minLength={8} autoComplete="new-password" value={f.confirm} onChange={up("confirm")} testid="change-confirm-input" /></Field>
        <button disabled={busy} className="btn-xd w-full" data-testid="change-password-submit">{t("pwchange.submit")}</button>
        <button type="button" onClick={async () => { await logout(); navigate("/login", { replace: true }); }} className="block text-sm text-zinc-500 hover:text-white" data-testid="change-password-logout">{t("auth.logout")}</button>
      </form>
    </AuthFrame>
  );
}
